<!--
  reservation-create-modal.vue

  2026-01-02: Fixed currency symbol display to respect selected currency in toMoney calls
-->
<template>
  <v-dialog
    v-model="dialog"
    :persistent="true"
    max-width="900px"
    :fullscreen="isMobile"
  >
    <template #activator="props">
      <slot name="customBtn" v-bind="props" />
      <v-tooltip v-if="isEditMode && !$scopedSlots.customBtn" bottom>
        <template #activator="{ on: tooltipOn }">
          <v-btn
            depressed
            icon
            color="info"
            class="btn-border"
            v-on="{ ...props.on, ...tooltipOn }"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('Edit Reservation') }}</span>
      </v-tooltip>
      <v-btn
        v-else-if="!$scopedSlots.customBtn"
        small
        color="info"
        v-on="props.on"
      >
        <v-icon left small>mdi-plus</v-icon> {{ $t('Reservation') }}
      </v-btn>
    </template>

    <v-sheet class="mx-auto" height="100%">
      <v-stepper v-model="steps" elevation="0" vertical class="pa-4">
        <div class="text-h5 font-weight-black text-center">
          {{
            isEditMode
              ? $t('Edit Internal Reservation')
              : $t('Create Reservation')
          }}
        </div>
        <v-stepper-step
          complete-icon="check"
          :complete="steps > 1"
          step="1"
          class="ps-0"
        >
          {{ $t('Reservation Details') }}
        </v-stepper-step>
        <v-stepper-content step="1" class="pa-0 ma-0">
          <v-form ref="form" @submit.prevent="createOrUpdate">
            <v-sheet class="pa-2 my-3 secondary lighten-3">
              <div class="text-h6 ps-2 py-6 font-weight-black">
                {{ $t('Reservation') }}
              </div>
              <v-row v-if="isEditMode" no-gutters class="mb-3">
                <v-col cols="12" class="px-2">
                  <v-checkbox
                    v-model="preservePrice"
                    :label="$t('Keep original pricing')"
                    :hint="
                      $t(
                        'When checked, prices won\'t automatically update if you modify the reservation dates or number of guests'
                      )
                    "
                    persistent-hint
                    dense
                    class="mt-0 mb-2"
                    color="primary"
                  />
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col class="px-2" cols="12">
                  <v-select
                    v-if="!isHotelReception && !isOwnerVacation"
                    v-model="reservation.status"
                    outlined
                    dense
                    :disabled="
                      isAgent || (isEditMode && !reservation.is_editable)
                    "
                    :items="statusItems"
                    @change="onStatusChange"
                  />
                  <v-select
                    v-if="
                      reservation.status === 'reserved' || isAgentReservation
                    "
                    v-model="reservation.reserved_expiration"
                    :label="$t('Expire after')"
                    outlined
                    dense
                    :items="[
                      { text: $t('1 day'), value: 24 },
                      { text: $t('2 days'), value: 48 },
                      { text: $t('3 days'), value: 72 },
                    ]"
                  />
                  <v-select
                    v-if="
                      !isHotelReception &&
                      !isOwnerVacation &&
                      !isAgentReservation &&
                      !isEditMode
                    "
                    v-model="reservation.reservation_type"
                    outlined
                    dense
                    :items="[
                      { text: $t('STR'), value: null },
                      { text: $t('Long Term'), value: 'long_term' },
                    ]"
                  />
                  <v-autocomplete
                    v-if="isAgentReservation && !isUserAgent"
                    v-model="reservation.agent"
                    :items="agents"
                    :rules="[required]"
                    item-value="id"
                    item-text="name"
                    return-object
                    no-filter
                    outlined
                    dense
                    clearable
                    :label="$t('Select Agent')"
                    @change="onAgentChange"
                  >
                  </v-autocomplete>
                  <v-combobox
                    v-if="!isEditMode && !isInvestor"
                    v-model="reservation.source"
                    :items="availableSources"
                    :label="$t('Source')"
                    :hint="$t('Select from the list or type a new source')"
                    persistent-hint
                    outlined
                    dense
                    clearable
                    :rules="[validateSource]"
                    :disabled="!channels.length"
                  />
                  <div
                    v-if="reservation.source === 'Owner' || isOwnerVacation"
                    class="mb-3"
                  >
                    <v-checkbox
                      v-model="reservation.extra_data.skip_automated_tasks"
                      :label="$t('Skip automated tasks creation')"
                      :hint="
                        $t(
                          'When checked, automated tasks (cleaning, maintenance, etc.) will not be created for this owner reservation'
                        )
                      "
                      persistent-hint
                      dense
                      class="mt-0 mb-2"
                      color="primary"
                    />
                    <v-checkbox
                      v-model="reservation.extra_data.owner_is_not_guest"
                      :label="$t('Owner is not the guest')"
                      :hint="
                        $t(
                          'Relevant for custom automated messages only. (reservation will not be charged)'
                        )
                      "
                      persistent-hint
                      dense
                      class="mt-0 mb-2"
                      color="primary"
                    />
                  </div>
                  <v-autocomplete
                    v-if="!isInvestor"
                    v-model="reservation.listing_id"
                    :label="$t('Listing')"
                    :items="listingsPicker"
                    dense
                    :rules="[required]"
                    item-text="nickname"
                    item-value="id"
                    outlined
                    @change="onListingChange"
                  />
                </v-col>
                <v-col class="px-2" cols="6" md="10">
                  <date-picker
                    v-model="reservation.range"
                    :range="true"
                    :prevent-same-day="true"
                    hide-details="auto"
                    :rules="[required, dateRange]"
                    :allowed-dates="isStr ? allowedDates : undefined"
                    :event-color="eventColors"
                    :label="$t('Dates')"
                  />
                </v-col>
                <v-col class="px-2" cols="6" md="2">
                  <v-text-field
                    v-model.number="reservation.guests_count"
                    :label="$t('No. Guests')"
                    :rules="[required, isPositive]"
                    :hint="
                      listing && parseInt(listing.accommodates) > 0
                        ? $t('Up to {count} guests', {
                            count: listing.accommodates,
                          })
                        : null
                    "
                    outlined
                    dense
                    :max="20"
                    type="number"
                  />
                </v-col>
                <v-col
                  v-if="isAdmin && isPastReservation"
                  cols="12"
                  class="px-2 pt-0 mt-n5 mb-4"
                >
                  <span class="text-caption grey--text text--darken-1">
                    <v-icon x-small color="orange darken-1" class="mr-1">
                      mdi-information-outline
                    </v-icon>
                    {{ $t('Reservation dates are in the past') }}
                  </span>
                </v-col>
                <v-col class="px-2" cols="12">
                  <v-textarea
                    v-model="reservation.dvr_notes"
                    :label="$t('Notes')"
                    outlined
                    dense
                    rows="3"
                  />
                </v-col>
                <v-col class="px-2" cols="12">
                  <v-combobox
                    v-model="reservation.tags"
                    :items="availableReservationTags"
                    :label="$t('Tags')"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
                <v-col
                  v-if="isOwnerVacation && reservation.range"
                  cols="12"
                  class="px-2"
                >
                  <v-alert
                    dense
                    outlined
                    border="left"
                    color="info"
                    icon="mdi-information-outline"
                    class="my-1"
                    elevation="0"
                  >
                    <span class="text-body-2">
                      {{
                        $t('owner_vacation_blocked_revenue_notice', {
                          amount: toMoney(blockedAmount, {
                            currency: selectedCurrency,
                          }),
                        })
                      }}
                    </span>
                  </v-alert>
                </v-col>
              </v-row>
            </v-sheet>
            <v-sheet
              v-if="!isAgentReservation && !isEditMode && !isUserAgent"
              class="pa-2 secondary my-3 lighten-3"
            >
              <div class="text-h6 ps-2 py-6 font-weight-black">
                {{ $t('Guest') }}
              </div>
              <reservation-guest-search
                :reservation.sync="reservation"
                :is-owner-vacation="!!isOwnerVacation"
              />
              <v-checkbox
                v-model="reservation.extra_data.is_local"
                :label="$t('Local citizen')"
                dense
                class="px-2"
                @change="onLocalCitizenChange"
              />
            </v-sheet>
            <v-sheet
              v-if="!isOwnerVacation"
              class="pa-2 secondary my-3 lighten-3"
            >
              <div class="text-h6 ps-2 py-6 font-weight-black">
                {{ $t('Financial') }}
              </div>
              <v-row no-gutters>
                <v-col class="px-2" cols="12" md="6">
                  <currency-picker
                    v-model="selectedCurrency"
                    :slim="true"
                    :disabled="pricingLoading || !reservation.listing_id"
                    :hint="$t('Select the currency for this reservation')"
                    @input="onCurrencyChange"
                  />
                </v-col>
                <v-col
                  v-if="exchangeRate !== 1 && baseCurrency"
                  cols="12"
                  md="6"
                  class="px-2 d-flex align-center"
                >
                  <v-chip small color="info" outlined>
                    1 {{ baseCurrency }} = {{ exchangeRate.toFixed(4) }}
                    {{ selectedCurrency }}
                  </v-chip>
                </v-col>
              </v-row>
              <v-row no-gutters class="mt-2">
                <v-col class="px-2" cols="6">
                  <v-text-field
                    v-model.number="reservation.origin_fare_accommodation"
                    :label="$t('Accommodation fare')"
                    :rules="[required, nonNegative]"
                    outlined
                    :disabled="
                      isAgent ||
                      (isEditMode && !reservation.is_editable) ||
                      pricingLoading
                    "
                    type="number"
                    dense
                    @input="manualFinancialChange"
                  />
                </v-col>
                <v-col class="px-2" cols="6">
                  <v-text-field
                    v-model.number="reservation.fare_cleaning"
                    :disabled="
                      isAgent ||
                      (isEditMode && !reservation.is_editable) ||
                      pricingLoading
                    "
                    :label="$t('Cleaning fare')"
                    outlined
                    type="number"
                    dense
                    @input="manualFinancialChange"
                  />
                </v-col>
                <v-col
                  v-if="
                    reservation.fees_items?.length ||
                    reservation.tax_lines?.length
                  "
                  cols="12"
                >
                  <v-checkbox
                    v-if="!isAgent"
                    v-model="reservation.fees_tax_overridden"
                    :label="$t('Override fees/taxes')"
                    dense
                  />
                </v-col>
                <v-col
                  v-for="fee in reservation.fees_items"
                  :key="fee.title"
                  class="px-2"
                  cols="6"
                >
                  <v-text-field
                    v-model.number="fee.amount"
                    :label="fee.title"
                    outlined
                    :disabled="!reservation.fees_tax_overridden"
                    type="number"
                    dense
                    @change="feeChange"
                  />
                </v-col>
                <v-col
                  v-for="tax in reservation.tax_lines"
                  :key="tax.title"
                  class="px-2"
                  cols="6"
                >
                  <v-text-field
                    v-model.number="tax.amount"
                    :disabled="!reservation.fees_tax_overridden"
                    :label="`${tax.title} ${
                      tax.vat_inclusive ? '(VAT Inc)' : ''
                    }`"
                    outlined
                    type="number"
                    dense
                    @change="feeChange"
                  />
                </v-col>
                <v-col
                  v-for="discount in reservation.periodic_discount_breakdown"
                  :key="discount.title"
                  class="px-2 text-right"
                  cols="12"
                >
                  <span class="text-caption grey--text"
                    >{{
                      toMoney(discount.amount, { currency: selectedCurrency })
                    }}
                    {{ discount.title }} {{ $t('(included)') }}</span
                  >
                </v-col>
              </v-row>
              <v-sheet v-if="!isStr" class="secondary lighten-3">
                <div
                  class="text-h6 ps-2 py-6 font-weight-black d-flex align-center"
                >
                  <span>{{ $t('Commissions') }}</span>
                  <v-switch
                    v-model="showCommissions"
                    class="mt-0 ml-4"
                    :label="$t('Show Commission Details')"
                    dense
                    hide-details
                  />
                </div>
                <v-row v-if="showCommissions" no-gutters>
                  <v-col class="px-2" cols="4">
                    <v-text-field
                      v-model.number="reservation.management_commission"
                      :label="$t('Management Commission')"
                      outlined
                      type="number"
                      dense
                      @input="
                        onCommissionChange(
                          $event,
                          reservation.management_commission
                        )
                      "
                    />
                  </v-col>
                  <v-col class="px-2" cols="4">
                    <v-text-field
                      v-model.number="reservation.client_profit"
                      :label="$t('Owner Profit')"
                      outlined
                      type="number"
                      dense
                      @input="
                        onCommissionChange($event, reservation.client_profit)
                      "
                    />
                  </v-col>
                  <v-col class="px-2" cols="4">
                    <v-text-field
                      v-model.number="reservation.net_income"
                      :label="$t('Net Income')"
                      outlined
                      type="number"
                      dense
                      @input="
                        onCommissionChange($event, reservation.net_income)
                      "
                    />
                  </v-col>
                </v-row>
              </v-sheet>
              <div class="px-2 text-end">
                <div v-if="needsFinancialRecalculation" class="text-right mt-2">
                  <v-btn
                    color="warning"
                    small
                    class="mb-2"
                    elevation="2"
                    @click="applyFinancialChanges"
                  >
                    <v-icon x-small left color="white">mdi-alert</v-icon>
                    {{ $t('APPLY CHANGES:') }}
                    {{ toMoney(total, { currency: selectedCurrency }) }}
                  </v-btn>
                </div>
                <div v-else>
                  <span class="font-weight-medium text-subtitle-2 mx-1">{{
                    $t('Total:')
                  }}</span
                  >{{ toMoney(total, { currency: selectedCurrency }) }}
                  <span v-if="isVatInc" class="text-caption secondary--text">
                    ({{ $t('VAT Inc') }} {{ vatRate }}%)
                  </span>
                </div>
              </div>
            </v-sheet>
            <div class="text-right py-2">
              <v-btn
                class="ml-auto mr-2"
                color="primary"
                outlined
                @click="close"
              >
                {{ $t('Cancel') }}
              </v-btn>
              <v-btn
                color="primary"
                :loading="loading"
                class="ml-auto"
                :disabled="pricingLoading || needsFinancialRecalculation"
                type="submit"
              >
                {{ submitButtonText }}
              </v-btn>
            </div>
          </v-form>
        </v-stepper-content>
        <v-stepper-step
          v-if="isStr && !isOwnerVacation && !isAgentReservation && !isEditMode"
          complete-icon="check"
          :complete="steps > 2"
          step="2"
          class="ps-0"
        >
          {{ $t('Payment Details') }}
        </v-stepper-step>
        <v-stepper-content
          v-if="isStr && !isEditMode"
          step="2"
          class="ma-0 pa-0"
        >
          <stripe-element
            v-if="intentId"
            :button-text="$t('PAY NOW')"
            :email="reservation.email"
            :public-key="publicKey"
            :intent-id="intentId"
            :client-secret="clientSecret"
            @payment-success="paymentSuccess"
            @payment-failed="paymentFailed"
            @payment-processing="paymentProcess"
          >
            <template #disclaimer>
              <div v-if="amount" class="text-right py-2">
                <tag size="sm" :inverted="true" color="warning">
                  <span class="text-subtitle-2">
                    {{
                      $t(
                        '* You are about to charge third of the reservation price.'
                      )
                    }}
                    <strong class="mx-2">
                      {{ toMoney(amount, { currency: selectedCurrency }) }}
                    </strong>
                  </span>
                </tag>
              </div>
            </template>
          </stripe-element>
          <div class="text-right py-2">
            <v-btn
              color="success"
              :loading="loading"
              class="ml-auto"
              @click="skipPayment"
            >
              {{ $t('Skip without pay') }}
            </v-btn>
          </div>
        </v-stepper-content>
      </v-stepper>
    </v-sheet>
  </v-dialog>
</template>

<script>
import FormRules from 'components/mixins/form-rules-mixin'
import CommonFunctions from 'components/mixins/common_functions'
import DatePicker from 'components/form-fields/date-picker'
import { mapGetters, mapState, mapActions } from 'vuex'
import Toaster from '@/utils/toaster'
import StripeElement from 'components/stripe/stripe-element'
import FormattersMixin from 'components/mixins/formatters-mixin'
import Tag from 'components/common/tag'
import PermissionsMixin from 'components/mixins/permissions-mixin'
import DeviceMixin from 'components/mixins/device-mixin'
import ReservationGuestSearch from 'components/reservation/reservation-guest-search.vue'
import CurrencyPicker from 'components/common/currency-picker.vue'
import { MARBELLA_DEMO_LISTING_ID } from '@/consts'
import axios from 'axios'
import debounce from 'lodash/debounce'
import TagsMixin from 'components/mixins/tags-mixin'

const DENIED_SOURCES = ['Airbnb', 'Booking.com']

export default {
  name: 'ReservationCreateModal',
  components: {
    Tag,
    StripeElement,
    DatePicker,
    ReservationGuestSearch,
    CurrencyPicker,
  },
  props: {
    preFilled: { type: Object, default: () => ({}) },
    ownerVacation: { type: Boolean, default: false },
    isEditMode: { type: Boolean, default: false },
    editReservation: { type: Object, default: null },
    color: { type: String, default: null },
    showBtnBorder: { type: Boolean, default: false },
  },
  mixins: [
    FormRules,
    CommonFunctions,
    FormattersMixin,
    PermissionsMixin,
    DeviceMixin,
    TagsMixin,
  ],
  data() {
    return {
      reservation: this.initialReservation(),
      listing: {},
      pricingLoading: false,
      currentRates: {},
      editModeInitiated: false,
      dialog: false,
      chosenGuest: null,
      guests: [],
      publicKey: null,
      clientSecret: null,
      createdReservation: {},
      intentId: null,
      steps: 1,
      search: '',
      amount: null,
      reservationLoading: false,
      phoneKey: 0,
      debouncer: debounce(this.priceChange, 500),
      showCommissions: false,
      editEnabled: false,
      needsFinancialRecalculation: false,
      preservePrice: false,
      selectedCurrency: null,
      baseCurrency: null,
      exchangeRate: 1,
    }
  },
  async mounted() {
    if (this.$store.state.currentListing) {
      this.listing = this.$store.state.currentListing
    }
    if (this.isAgent) {
      this.reservation.agent_id = this.currentUser().id
      this.reservation.status = 'agent-reserved'
    }
    this.fetchChannels()
    this.fetchReservationTags()
    if (this.reservation.listing_id) {
      this.currentRates = await this.$store.dispatch(
        'listings/getListingRates',
        this.reservation.listing_id
      )
      await this.refetchPricing()
    }
  },
  computed: {
    ...mapGetters(['listingsPicker']),
    ...mapState('reservation', ['channels']),
    ...mapState('users', ['allUsers']),
    availableSources() {
      return this.channels.filter(
        channel => !this.deniedSources.includes(channel)
      )
    },
    isOwnerVacation() {
      return this.reservation.source === 'Owner'
    },
    deniedSources() {
      return DENIED_SOURCES
    },
    isUserAgent() {
      return this.currentUser().role === 'agent'
    },
    agents() {
      return this.allUsers.filter(r => r.role === 'agent')
    },
    isVatInc() {
      return Boolean(this.vatInc)
    },
    vatRate() {
      return this.vatInc?.amount || 0
    },
    reservationDataString() {
      const r = this.reservation
      return `${r.listing_id}-${r.check_in}-${r.check_out}-${
        r.guests_count
      }-${r.range?.toString()}`
    },
    isStr() {
      return this.reservation.reservation_type !== 'long_term'
    },
    isPastReservation() {
      if (!this.reservation.range || this.reservation.range.length < 2) {
        return false
      }
      const today = this.$moment().startOf('day')
      const checkIn = this.$moment(this.reservation.range[0])
      const checkOut = this.$moment(this.reservation.range[1])
      return checkIn.isBefore(today) || checkOut.isBefore(today)
    },
    marbellaId() {
      return MARBELLA_DEMO_LISTING_ID
    },
    total() {
      return (
        (this.reservation.origin_fare_accommodation || 0) +
        (this.reservation.fare_cleaning || 0) +
        this.totalTax +
        this.totalFees
      )
    },
    totalTax() {
      return this.reservation.tax_lines
        .filter(tax => !tax.vat_inclusive && !tax.inclusive_fee)
        .reduce((acc, tax) => acc + (parseFloat(tax.amount) || 0), 0)
    },
    totalFees() {
      return this.reservation.fees_items.reduce(
        (acc, fee) =>
          fee.inclusive_fee ? acc : acc + (parseFloat(fee.amount) || 0),
        0
      )
    },
    totalInclusiveFees() {
      return this.reservation.tax_lines.reduce(
        (acc, fee) =>
          fee.inclusive_fee ? acc + (parseFloat(fee.amount) || 0) : acc,
        0
      )
    },
    blockedAmount() {
      if (!this.isOwnerVacation) return 0
      return (
        (this.reservation.origin_fare_accommodation || 0) +
        (this.reservation.fare_cleaning || 0) +
        this.totalTax +
        this.totalFees
      )
    },
    isAgentReservation() {
      return this.reservation.status === 'agent-reserved'
    },
    isReserved() {
      return this.reservation.agent_id || this.reservation.status === 'reserved'
    },
    isEditOwnerVacation() {
      return this.isEditMode && this.reservation.source === 'Owner'
    },
    businessModel() {
      if (!this.listing?.business_model_id) return null
      return this.$store.state.businessModel.businessModels.find(
        model => model.id === this.listing.business_model_id
      )
    },
    submitButtonText() {
      if (this.isEditMode) return this.$t('Update')
      return this.isStr && !this.isOwnerVacation
        ? this.$t('Continue')
        : this.$t('Create')
    },
    statusItems() {
      const baseItems = [
        { text: this.$t('Confirmed'), value: 'confirmed' },
        { text: this.$t('Inquiry'), value: 'inquiry' },
        { text: this.$t('Reserved'), value: 'reserved' },
        { text: this.$t('Agent Reserved'), value: 'agent-reserved' },
      ]
      if (
        this.isEditMode &&
        (this.reservation.status === 'reserved' ||
          this.reservation.status === 'expired')
      ) {
        baseItems.push({ text: this.$t('Expired'), value: 'expired' })
      }

      return baseItems
    },
  },
  watch: {
    search(val) {
      if (!val) {
        return
      }
      this.fetchEntriesDebounced(val)
    },
    async reservationDataString(_val) {
      if (!this.isEditMode || (this.isEditMode && this.editModeInitiated)) {
        if (!this.preservePrice) {
          this.debouncer.call(this, false)
        }
      }
    },
    async dialog(isOpen) {
      if (isOpen) {
        if (this.isEditMode && this.editReservation) {
          await this.initializeEditMode()
        } else if (!this.isEmpty(this.preFilled)) {
          await this.initializeFromPreFilled()
        }
      }
      if (this.isAgent) {
        const agent = this.currentUser()
        this.reservation.agent_id = agent.id
        this.reservation.status = 'agent-reserved'
        this.onAgentChange(agent)
      }
    },
    'reservation.source'(newSource) {
      if (newSource === 'Owner') this.fillOwnerFromListing()
    },
    listing() {
      if (this.reservation.source === 'Owner') this.fillOwnerFromListing()
    },
  },
  methods: {
    ...mapActions('reservation', ['fetchChannels']),
    ...mapGetters(['currentUser']),
    validateSource(v) {
      return (
        !v ||
        !this.deniedSources.includes((v || '').trim()) ||
        this.$t('This source is not allowed')
      )
    },
    manualFinancialChange(val) {
      if (isNaN(val)) return
      if (this.isEditOwnerVacation) {
        this.needsFinancialRecalculation = false
        return
      }

      this.needsFinancialRecalculation = true
    },
    applyFinancialChanges() {
      this.needsFinancialRecalculation = false
      // Update fare_accommodation accounting for inclusive/non-inclusive fees
      this.feeChange()
      this.debouncer.call(this, true)
      // Recalculate commissions based on manual values without refetching from API
      this.calculateCommissions()
    },
    invoiceChange(val) {
      if (isNaN(val)) {
        return
      }
      this.debouncer.call(this, true)
    },
    async refetchPricing(ovverideFA = true) {
      if (
        this.reservation.listing_id &&
        ((this.reservation.check_in && this.reservation.check_out) ||
          this.reservation.range?.length === 2) &&
        this.reservation.guests_count
      ) {
        this.pricingLoading = true
        const start_date = this.reservation.range[0]
        const end_date = this.reservation.range[1]

        // Convert fare values back to base currency if we're working in a different currency.
        // After a currency change, fare values in the UI are in the selected currency,
        // but the API expects them in the base currency.
        let fareToSend = null
        let cleaningToSend = null
        if (ovverideFA) {
          fareToSend = this.reservation.origin_fare_accommodation
          cleaningToSend = this.reservation.fare_cleaning
          if (this.exchangeRate && this.exchangeRate !== 1) {
            fareToSend =
              fareToSend != null ? fareToSend / this.exchangeRate : null
            cleaningToSend =
              cleaningToSend != null ? cleaningToSend / this.exchangeRate : null
          }
        }

        try {
          const {
            data: { info },
          } = await axios.get(
            `/api/reservations/listings/${this.reservation.listing_id}/pricing`,
            {
              params: {
                start_date,
                end_date,
                guests_count: this.reservation.guests_count,
                fare_accommodation: fareToSend,
                fare_cleaning: cleaningToSend,
                is_local: this.reservation.extra_data?.is_local,
                currency: this.selectedCurrency,
              },
            }
          )

          this.reservation.fare_accommodation = info.adjusted_fare_accommodation
          this.reservation.fare_cleaning = info.cleaning_fee
          this.reservation.total_taxes = info.taxes
          this.reservation.fees_items = info.fees_breakdown
          this.reservation.tax_lines = info.taxes_breakdown
          this.reservation.periodic_discount_breakdown =
            info.periodic_discount_breakdown || []
          this.reservation.origin_fare_accommodation = info.fare_accommodation
          this.vatInc = info.vat_inclusive

          // Update currency info from response
          if (!this.selectedCurrency && info.currency) {
            this.selectedCurrency = info.currency
          }
          this.baseCurrency = info.base_currency
          this.exchangeRate = info.exchange_rate || 1
          this.reservation.currency = info.currency

          this.reservation.vat_inclusive_rate = this.vatRate
          this.needsFinancialRecalculation = false
        } catch (e) {
          console.error('Failed to fetch pricing')
        }
        this.pricingLoading = false
      }
    },
    onCurrencyChange(currency) {
      this.selectedCurrency = currency
      this.reservation.currency = currency
      // Only refetch pricing if a currency is actually selected (not cleared)
      if (currency) {
        this.refetchPricing(true)
      }
    },
    async paymentSuccess() {
      Toaster.show([
        { type: 'success', text: this.$t('Payment done successfully') },
      ])
      this.$store.commit('updateLoading', false)
      this.$emit('charge-success')
      this.skipPayment()
    },
    paymentProcess() {
      this.$store.commit('updateLoading', true)
    },
    async paymentFailed() {
      Toaster.show([
        { type: 'error', text: this.$t('Payment failed to process') },
      ])
      this.$store.commit('updateLoading', false)
    },
    fetchEntriesDebounced(newValue) {
      // cancel pending call
      clearTimeout(this._timerId)

      // delay new call 500ms
      this._timerId = setTimeout(async () => {
        this.guests = await this.$store.dispatch('guests/fetchGuests', {
          term: newValue,
        })
      }, 1000)
    },
    async onListingChange() {
      this.listing = this.listingsPicker.find(
        l => l.id === this.reservation.listing_id
      )
      this.currentRates = await this.$store.dispatch(
        'listings/getListingRates',
        this.reservation.listing_id
      )
      if (this.reservation.listing_id === this.marbellaId) {
        this.reservation = { ...this.reservation, ...this.prefilledDemo() }
      }
    },
    feeChange() {
      // fare_accommodation = origin_fare - inclusive_fees + non_inclusive_fees
      // Inclusive fees are already part of origin_fare, so we subtract them
      // Non-inclusive fees are added on top
      this.reservation.fare_accommodation =
        this.reservation.origin_fare_accommodation -
        this.totalInclusiveFees +
        this.totalFees
    },
    async priceChange(overrideFA = true) {
      if (!this.reservation.listing_id) return
      await this.refetchPricing(overrideFA)
      if (!this.isOwnerVacation) {
        this.listing = this.listingsPicker.find(
          l => l.id === this.reservation.listing_id
        )
      }

      this.calculateCommissions()
    },
    allowedDates(date) {
      // Admins can always select past dates
      const isPastDate = this.$moment(date).isBefore(this.$moment(), 'day')
      if (this.isAdmin && isPastDate) {
        return true
      }

      // Check if date is within current reservation (making it selectable for editing)
      const timezone = this.editReservation?.listing?.timezone
      const isCurrentReservationDate =
        this.isEditMode &&
        this.reservation.is_editable &&
        this.$moment(date).isBetween(
          this.parseDateTZ(this.reservation.check_in, 'YYYY-MM-DD', timezone),
          this.parseDateTZ(this.reservation.check_out, 'YYYY-MM-DD', timezone),
          null,
          '[]'
        )

      // Original availability check
      const dayBefore = this.$moment(date)
        .subtract(1, 'day')
        .format('YYYY-MM-DD')

      const isAvailable =
        this.currentRates[date]?.status === 'available' ||
        (this.currentRates[dayBefore] &&
          this.currentRates[date] &&
          this.currentRates[date].status !== 'available' &&
          this.currentRates[dayBefore].status === 'available')

      // Return true if the date is either available OR part of the current reservation
      return isCurrentReservationDate || isAvailable
    },
    eventColors(date) {
      if (
        this.currentRates[date] &&
        this.currentRates[date].status === 'available'
      ) {
        return 'green lighten-1'
      }
      return ''
    },
    close() {
      this.$refs.form?.reset()
      this.$nextTick(() => {
        this.reservation = this.initialReservation()
        this.createdReservation = {}
        this.amount = null
        this.publicKey = null
        this.clientSecret = null
        this.intentId = null
        this.preservePrice = false
        this.selectedCurrency = null
        this.baseCurrency = null
        this.exchangeRate = 1
        this.dialog = false
      })
    },
    skipPayment() {
      this.$emit('oncreate', this.createdReservation.reservation_guesty_id)
      this.close()
    },
    allowedDatesOwner(val) {
      return (
        !this.listing.allowed_vacation_dates[val] &&
        this.listing.days_rates[val] &&
        this.listing.days_rates[val].status === 'available'
      )
    },
    async createReservation(payload) {
      const { res, stripe_public_key, client_secret, intent_id, amount } =
        await this.$store.dispatch('reservation/createReservation', payload)
      if (this.isOwnerVacation) {
        await this.$store.dispatch('changeListing', {
          listingId: this.listing.id,
        })
      }
      if (!res) {
        return
      }
      this.createdReservation = res
      if (this.createdReservation && !intent_id) {
        this.skipPayment()
        return
      }
      this.amount = amount
      this.clientSecret = client_secret
      this.publicKey = stripe_public_key
      this.intentId = intent_id
      this.steps = 2
    },
    async createInternalReservation(payload) {
      if (this.isAgentReservation) {
        payload.status = 'reserved'
      }
      const { res } = await this.$store.dispatch(
        'reservation/createInternalReservation',
        payload
      )
      this.createdReservation = res
      this.skipPayment()
    },
    async updateInternalReservation(payload) {
      await this.$store.dispatch(
        'reservation/updateInternalReservation',
        payload
      )
      this.$emit('change')
      this.close()
    },
    async createOrUpdate() {
      if (this.$refs.form.validate()) {
        this.$store.commit('updateLoading', true)
        if (
          this.chosenGuest &&
          this.chosenGuest.emails[0] !== this.reservation.email
        ) {
          this.reservation.guest_id = null
        }
        this.reservation.fees_items = this.reservation.fees_items.filter(
          fee => fee.amount
        )
        this.reservation.tax_lines = this.reservation.tax_lines.filter(
          tax => tax.amount
        )
        if (this.reservation.source) {
          this.reservation.source = this.reservation.source.trim()
        }
        this.reservation.total_taxes = this.totalTax
        if ((this.isReserved || this.isAgentReservation) && !this.isEditMode) {
          return await this.createInternalReservation(this.reservation)
        }
        const isUnConfirmedHostaway =
          this.isHostaway && this.status !== 'confirmed'
        if (this.isEditMode) {
          await this.updateInternalReservation(this.reservation)
        } else if (this.isOwnerVacation) {
          const data = {
            ...this.reservation,
            listing_id: this.listing.id,
            fare_accommodation: 0,
            fare_cleaning: 0,
            source: 'Owner',
            ignore_terms: true,
            status: 'confirmed',
            guests_count: this.reservation.guests_count || 1,
          }
          await this.createReservation(data)
        } else if (this.isStr && !this.isOptima && !isUnConfirmedHostaway) {
          await this.createReservation(this.reservation)
        } else {
          await this.createInternalReservation(this.reservation)
        }
      }
    },
    initialReservation() {
      this.phoneKey++
      this.needsFinancialRecalculation = false
      return {
        listing_id: this.preFilled?.listing_id || null,
        reservation_type: null,
        fare_accommodation: null,
        origin_fare_accommodation: null,
        fare_cleaning: null,
        management_commission: 0,
        client_profit: 0,
        net_income: 0,
        processing_fee: 0,
        fees_tax_overridden: false,
        guests_count: 1,
        status: 'confirmed',
        source: this.ownerVacation ? 'Owner' : 'manual',
        check_in: null,
        check_out: null,
        total_taxes: 0,
        fees_items: [],
        tax_lines: [],
        periodic_discount_breakdown: [],
        range: [],
        first_name: this.ownerVacation ? this.currentUser().first_name : null,
        last_name: this.ownerVacation ? this.currentUser().last_name : null,
        email: this.ownerVacation ? this.currentUser().email : null,
        phone: this.ownerVacation ? this.currentUser().phone : undefined,
        reserved_expiration: 24,
        extra_data: {
          is_local: false,
        },
        tags: [],
      }
    },
    prefilledDemo() {
      return {
        range: [
          this.$moment().utc().add(2, 'days').format('YYYY-MM-DD'),
          this.$moment().utc().add(4, 'days').format('YYYY-MM-DD'),
        ],
        guests_count: 2,
        note: 'Demo booking',
        phone: '',
        email: 'demo+' + Math.random() * 1000000 + '@boomnow.com',
      }
    },
    onStatusChange() {
      this.reservation.reservation_type = null
    },
    onAgentChange(agent) {
      this.reservation.phone = agent.phone
      this.reservation.first_name = agent.first_name
      this.reservation.last_name = agent.last_name
      this.reservation.agent_id = agent.id
      this.reservation.agent_name = agent.name
      this.reservation.email = agent.email
      this.reservation.source = 'Agent'
    },
    fillOwnerFromListing() {
      const owner = this.listing?.main_owner
      if (!owner) return
      if (owner.first_name) this.reservation.first_name = owner.first_name
      if (owner.last_name) this.reservation.last_name = owner.last_name
      if (owner.email) this.reservation.email = owner.email
      if (owner.phone) this.reservation.phone = owner.phone
    },
    calculateCommissions() {
      if (!this.listing) return

      const fareAccommodation = this.reservation.fare_accommodation || 0

      if (this.businessModel) {
        // Prepare variables for formula evaluation
        const reservationVars = {
          fare_accommodation: fareAccommodation,
          cleaning_fee: this.reservation.fare_cleaning || 0,
          total_taxes: this.totalTax || 0,
          company_fees: this.totalFees || 0,
          commission_percent: this.listing.commission || 0,
          price: this.total || 0,
          host_payout: this.total || 0,
          host_channel_fee: 0,
          reported_channel_fee: 0,
          guests_count: this.reservation.guests_count || 0,
        }

        try {
          let netIncomeFormula = this.businessModel.net_income_formula
          Object.keys(reservationVars).forEach(key => {
            const regex = new RegExp(`\\b${key}\\b`, 'g')
            netIncomeFormula = netIncomeFormula.replace(
              regex,
              reservationVars[key]
            )
          })
          this.reservation.net_income = this.round(eval(netIncomeFormula), 2)
          reservationVars.net_income = this.reservation.net_income
          // Evaluate management commission formula
          let managementFormula =
            this.businessModel.management_commission_formula
          Object.keys(reservationVars).forEach(key => {
            const regex = new RegExp(`\\b${key}\\b`, 'g')
            managementFormula = managementFormula.replace(
              regex,
              reservationVars[key]
            )
          })
          this.reservation.management_commission = this.round(
            eval(managementFormula),
            2
          )
          reservationVars.management_commission =
            this.reservation.management_commission

          // Evaluate client profit formula
          let clientProfitFormula = this.businessModel.owner_revenue_formula
          Object.keys(reservationVars).forEach(key => {
            const regex = new RegExp(`\\b${key}\\b`, 'g')
            clientProfitFormula = clientProfitFormula.replace(
              regex,
              reservationVars[key]
            )
          })
          this.reservation.client_profit = this.round(
            eval(clientProfitFormula),
            2
          )
          reservationVars.client_profit = this.reservation.client_profit
        } catch (error) {
          console.error('Error evaluating business model formulas:', error)
        }
      } else {
        this.reservation.net_income = this.reservation.fare_accommodation
        // Use listing commission if no business model
        this.reservation.management_commission = this.round(
          this.listing.commission * this.reservation.net_income,
          2
        )
        this.reservation.client_profit = this.round(
          this.reservation.net_income - this.reservation.management_commission,
          2
        )
      }
    },
    async initializeEditMode() {
      const { cleaning_fee, ...editRes } = this.editReservation
      this.reservation = {
        ...editRes,
        origin_fare_accommodation: editRes.fare_accommodation,
        fare_cleaning: +cleaning_fee,
        tax_lines: editRes.invoice_items.filter(
          item =>
            item.type !== 'ACCOMMODATION_FARE' && item.type !== 'CLEANING_FEE'
        ),
        range: [
          this.parseDateTZ(
            editRes.check_in,
            'YYYY-MM-DD',
            editRes.listing.timezone
          ),
          this.parseDateTZ(
            editRes.check_out,
            'YYYY-MM-DD',
            editRes.listing.timezone
          ),
        ],
      }
      this.listing = editRes.listing
      // Initialize currency from existing reservation
      if (editRes.currency) {
        this.selectedCurrency = editRes.currency
      }
      this.currentRates = await this.$store.dispatch(
        'listings/getListingRates',
        this.reservation.listing_id
      )
      this.editModeInitiated = true
    },
    async initializeFromPreFilled() {
      const { first_name, last_name, email, phone, conversation_id } =
        this.preFilled
      // Only overwrite guest fields if they exist in preFilled
      if (first_name !== undefined) this.reservation.first_name = first_name
      if (last_name !== undefined) this.reservation.last_name = last_name
      if (phone !== undefined) this.reservation.phone = phone
      if (email !== undefined) this.reservation.email = email
      if (conversation_id !== undefined)
        this.reservation.conversation_id = conversation_id
      this.reservation.guests_count = this.preFilled
        ? +this.preFilled.guests_count
        : null
      this.reservation.range = this.preFilled.range ? this.preFilled.range : []
      this.reservation.guests_count = this.preFilled
        ? +this.preFilled.guests_count
        : null
      if (this.preFilled.listingId) {
        this.reservation.listing_id = this.preFilled
          ? +this.preFilled.listingId
          : null
        this.listing = this.listingsPicker.find(
          l => l.id === this.reservation.listing_id
        )
        this.currentRates = await this.$store.dispatch(
          'listings/getListingRates',
          this.reservation.listing_id
        )
        await this.refetchPricing()
      }
    },
    onCommissionChange(newVal, currentVal) {
      if (newVal !== currentVal) {
        this.reservation.pms_unsync = true
      }
    },
    onLocalCitizenChange() {
      if (!this.preservePrice) {
        this.refetchPricing()
      }
    },
  },
}
</script>

<style scoped>
.btn-border {
  border: solid 1px;
}
</style>
